import { Dispatch } from "redux";
import _ from "lodash";

import {
  BLENDING_IMPORT,
  BLENDING_SET_ROUND,
  BLENDING_RESET_ALPHABET_LIST,
  BLENDING_DRAG_CARD,
  BLENDING_CHECK_ANSWER,
  BLENDING_CANCEL_RESPONSE,
  BLENDING_FINISHED,
  BLENDING_PLAY_AGAIN,
  BLENDING_SET_ORIGINAL_ALPHABET_LIST,
  BLENDING_RESET_ANSWERED,
  BLENDING_SET_IS_SINGLE_ROW,
} from "./actionIdentifiers";
import { displayLetter, findJsonValue } from "./../../util/Util";
import { State } from "../store";
import { srcPath, get } from "../../util/Path";
import { gameConfigLoad } from "./shared";

export const blendingAudio = new Audio();

const audioSrc = {
  correct: srcPath("shared/clapping.mp3"),
  wrong: srcPath("blending/wrong.wav"),
};

export const importWords = (grade: number, unit: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const res = await get(`config/blending.json`);
    const blendingList = res.data;
    const alphabets = findJsonValue(
      blendingList,
      "alphabets",
      { grade, unit },
      true
    );
    const words = findJsonValue(blendingList, "words", { grade, unit }, true);

    let maxAlphabet = findJsonValue(blendingList, "maxAlphabet", {
      grade,
      unit,
    });
    if (!maxAlphabet) maxAlphabet = 0;

    let disableShuffle =
      findJsonValue(blendingList, "disable_shuffle", {
        grade,
        unit,
      }) || false;

    dispatch({
      type: BLENDING_IMPORT,
      payload: { alphabets, words, maxAlphabet, disableShuffle },
    });

    dispatch(gameConfigLoad());
  };
};

const setOriginalAlphabetList = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { answer, maxAlphabet, round, words } = getState().blending;
    let { disableShuffle } = getState().blending;
    let alphabets: any[] = _.cloneDeep(getState().blending.alphabets);
    const wordObj = words[round - 1];

    if (wordObj && typeof wordObj.disable_shuffle !== "undefined") {
      disableShuffle = wordObj.disable_shuffle;
    }
    if (wordObj && typeof wordObj.unit_alphabets !== "undefined") {
      alphabets = _.cloneDeep(wordObj.unit_alphabets);
    }

    const blanks = [...getState().blending.blanks];
    const blankedAnswers = [...new Set(blanks.map((b) => answer[b]))];

    if (disableShuffle) {
      dispatch({
        type: BLENDING_SET_ORIGINAL_ALPHABET_LIST,
        payload: alphabets,
      });
      return;
    }

    let originalList: any[];

    if (alphabets.length > 0 && Array.isArray(alphabets[0])) {
      originalList = _.cloneDeep(alphabets);
      originalList.forEach((arr) => {
        arr.sort(() => Math.random() - 0.5);
      });
    } else {
      originalList = alphabets.filter((alphabet: string) => {
        return !blankedAnswers.includes(alphabet);
      });
      originalList.sort(() => Math.random() - 0.5);

      let maxLength = maxAlphabet - blankedAnswers.length;
      if (maxLength < 0) maxLength = 0;

      if (originalList.length > maxLength) {
        originalList = originalList.slice(0, maxLength);
      }
      originalList = originalList.concat(blankedAnswers);
      originalList.sort(() => Math.random() - 0.5);
    }

    dispatch({
      type: BLENDING_SET_ORIGINAL_ALPHABET_LIST,
      payload: originalList,
    });
  };
};

const resetAlphabetList = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const originalAlphabetList = _.cloneDeep(
      getState().blending.originalAlphabetList
    );
    dispatch({
      type: BLENDING_RESET_ALPHABET_LIST,
      payload: originalAlphabetList,
    });
  };
};

const resetAnswered = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const answer = getState().blending.answer;
    const blanks = [...getState().blending.blanks];
    const answered = Array(answer.length)
      .fill(1)
      .map((_, i) => {
        return blanks.includes(i) ? null : answer[i];
      });
    dispatch({ type: BLENDING_RESET_ANSWERED, payload: answered });
  };
};

export const setRound = (round: number) => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { value, blanks } = [...getState().blending.words][round - 1];

    dispatch({
      type: BLENDING_SET_ROUND,
      payload: { round, answer: value, blanks },
    });

    dispatch(setOriginalAlphabetList());
    dispatch(resetAlphabetList());
    dispatch(resetAnswered());
  };
};

export const dragCard = (
  alphabet: string,
  dragOnBoard: boolean,
  dragIndex: number,
  dropOnBoard: boolean,
  dropIndex: number
) => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const alphabetList: any[] = _.cloneDeep(getState().blending.alphabetList);
    const answered = [...getState().blending.answered];

    if (dragOnBoard) {
      answered[dragIndex] = null;
    } else if (Array.isArray(alphabetList[0])) {
      let tempIndex = 0;
      alphabetList.some((arr, i) => {
        if (tempIndex + arr.length >= dragIndex + 1) {
          alphabetList[i][dragIndex - tempIndex] = null;
          return true;
        } else {
          tempIndex += arr.length;
          return false;
        }
      });
    } else {
      alphabetList[dragIndex] = null;
    }

    if (dropOnBoard) {
      answered[dropIndex] = alphabet;
    } else if (Array.isArray(alphabetList[0])) {
      let tempIndex = 0;
      alphabetList.some((arr, i) => {
        if (tempIndex + arr.length >= dropIndex + 1) {
          alphabetList[i][dropIndex - tempIndex] = alphabet;
          return true;
        } else {
          tempIndex += arr.length;
          return false;
        }
      });
    } else {
      alphabetList[dropIndex] = alphabet;
    }

    dispatch({ type: BLENDING_DRAG_CARD, payload: { alphabetList, answered } });
  };
};

export const checkAnswer = () => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { grade } = getState().shared;
    const answer = getState().blending.answer;
    const answered = [...getState().blending.answered];

    let answerString = Array.isArray(answer)
      ? displayLetter(answer.join(""))
      : displayLetter(answer);
    let answeredString = answered.map((a) => displayLetter(a)).join("");

    if (grade == 3) {
      answerString = Array.isArray(answer) ? answer.join("") : answer;
      answeredString = answered.join("");
    }

    if (!answered.includes(null)) {
      const response = answeredString === answerString ? "correct" : "wrong";
      blendingAudio.src = audioSrc[response];
      blendingAudio.play();

      dispatch({ type: BLENDING_CHECK_ANSWER, payload: response });

      setTimeout(() => {
        dispatch({ type: BLENDING_CANCEL_RESPONSE });

        if (response === "correct") {
          const round = getState().blending.round;
          const totalRound = getState().blending.words.length;

          if (round !== totalRound) {
            dispatch(setRound(round + 1));
          } else {
            dispatch({ type: BLENDING_FINISHED });
          }
        } else {
          dispatch(resetAlphabetList());
          dispatch(resetAnswered());
        }
      }, 1000);
    }
  };
};

export const playAgain = () => {
  return (dispatch: Dispatch<any>) => {
    dispatch({ type: BLENDING_PLAY_AGAIN });
    dispatch(setRound(1));
  };
};

export const setIsSingleRow = (isSingleRow: boolean) => {
  return { type: BLENDING_SET_IS_SINGLE_ROW, payload: isSingleRow };
};
